import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { RxMinus, RxPlus } from 'react-icons/rx';
import { toast } from 'react-toastify';

import { Grid, IconButton, TextField } from '@mui/material';
import { DatePicker, DateValidationError } from '@mui/x-date-pickers';
import { create, get, initiateResetPassword, update } from 'api/api';
import ButtonCreateModalDialog from 'components/ButtonCreateModalDialog';
import InputImage from 'components/InputImage';
import InputSelect from 'components/inputSelect';
import ModalDialog from 'components/ModalDialog';
import Observacao from 'components/Observacao';
import { useAuth } from 'contexts/auth';
import { State } from 'types/list';
import { Fields } from 'types/table';
import { AppError } from 'utils/AppError';
import { handleApiError } from 'utils/handleApiError';
import { removeMasks, masks } from 'utils/masksManager';

import { AddInputButtonContainer } from './styles';

interface Props {
  id?: string | number;
  action: 'create' | 'update';
  open: boolean;
  setRefreshInput?: Dispatch<SetStateAction<boolean>>;
  onClose: () => Promise<void> | undefined;
  idColumn: string;
  value?: any;
  setValue?: any;
  getFields?:
    | ((field: any, selectedFazenda: any) => Promise<Fields[]>)
    | ((field: any) => Promise<Fields[]>);
  table: string;
  state: State;
  setState: Dispatch<SetStateAction<State>>;
}

interface ObservacaoStateProps {
  descricao: string | null;
  dataObservacao: string | null;
}

export default function CreateUpdateModalDialog(props: Props) {
  const reRef = useRef<ReCAPTCHA>();
  const { selectedFazenda } = useAuth();
  const form = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<any>({});
  const [fieldValues, setFieldValues] = useState<any>({});
  const [error, setError] = useState<any>({});
  const [refresh, setRefresh] = useState<boolean>(false);
  const [refreshInput, setRefreshInput] = useState<boolean>(false);
  const [action, setAction] = useState<'create' | 'update'>('create');
  const [newFields, setNewFields] = useState<Fields[]>([]);
  const { setState, state } = props;
  const [dateErrors, setDateErrors] = useState<{ [key: string]: DateValidationError | null }>({});
  const [observacao, setObservacao] = useState<ObservacaoStateProps[]>([
    { descricao: '', dataObservacao: '' },
  ]);

  const getDateErrorMessage = (fieldName: string): string => {
    const errorType = dateErrors[fieldName];
    switch (errorType) {
      case 'disablePast':
        return 'Data não pode ser do passado';
      case 'invalidDate':
        return 'Data inválida';
      case 'minDate':
        return 'Insira uma data posterior';
      case 'maxDate':
        return 'Insira uma data anterior';
      default:
        return '';
    }
  };

  const handleClose = async () => {
    setError({});
    setOpen(false);
    props.onClose();
  };

  const getPropsFields = async (field: any) => {
    if (!props.getFields) {
      return [];
    }

    return await props.getFields(field, selectedFazenda);
  };

  const addObservacao = () => {
    if (!observacao[observacao.length - 1].descricao) {
      toast.warn('O campo "Observação" não pode ser nulo');

      return;
    }

    const newObservacao = { descricao: '', dataObservacao: '' };
    setObservacao([...observacao, newObservacao]);
  };

  const removeObservacao = (i: number) => {
    const filterObservacao = observacao.filter((obs, index) => index !== i);
    setObservacao(filterObservacao);
  };

  const getFields = async () => {
    const fields = await getPropsFields({});
    return fields;
  };

  const setNewFieldsOnCreation = async () => {
    const fields = await getFields();
    await resetFields();

    setObservacao([{ descricao: '', dataObservacao: new Date().toISOString() }]);

    const hiddenFields = fields.filter((field) => field.type === 'hidden');
    const hiddenFieldsObject: any = {};

    hiddenFields.forEach(({ name, defaultValue }) => {
      hiddenFieldsObject[name] = defaultValue;
    });

    setNewFields(fields);
    setFieldValues({ ...hiddenFieldsObject });
  };

  const setNewFieldsOnUpdate = async () => {
    setState({ ...state, openBackdrop: true });

    try {
      const res = await get(`${props.table}/${props.id}`);

      setSelected(res.data);

      setObservacao(res.data.observacao || [{ descricao: '', dataObservacao: '' }]);

      const fields = await getPropsFields(res.data);
      const newFields = { id: props.id } as any;
      fields.forEach((field) => {
        newFields[field.name] = field.defaultValue;
      });

      setNewFields(fields);
      setFieldValues(newFields);
      setState({ ...state, openBackdrop: false });
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleOpenModal = async () => {
    setAction(props.action);
    setSelected({});

    if (props.action === 'update') {
      await setNewFieldsOnUpdate();
    } else {
      await setNewFieldsOnCreation();
    }
  };

  const getObjectData = async () => {
    const data = new FormData(form.current);

    const objData: any = {};

    data.forEach((value, key) => {
      if (value !== '') {
        objData[key] = value;
      }
    });

    if (fieldValues.id) {
      Object.assign(objData, { id: fieldValues.id });
    }

    const shouldIncludeObservacao = newFields.some((field) => field.type === 'observacao');

    if (shouldIncludeObservacao) {
      objData.observacao = observacao.filter((obs) => obs.descricao);
      delete objData.descricao;
      delete objData.dataObservacao;
    }

    return objData;
  };

  const getCleanData = async () => {
    const objData = await getObjectData();

    // Remove as máscaras antes de enviar a API
    const cleanData = removeMasks(objData);

    return cleanData;
  };

  const handleConfirm = async () => {
    const objData = await getObjectData();
    const cleanData = await getCleanData();

    try {
      setState({ ...state, openBackdrop: true });

      if (action === 'create') {
        const res = await create(props.table, cleanData);
        const data = res.data;
        setError(data.error ?? {});

        if (!data.error) {
          setState({
            ...state,
            openMsg: true,
            msg: 'Cadastro realizado com sucesso!',
            alertSeverity: 'success',
          });
          setRefresh(!refresh);
          props.onClose();
          setOpen(false);
          props.setRefreshInput && props.setRefreshInput(true);

          if (objData.cpf && objData.email) {
            const reToken = await reRef.current?.executeAsync();
            reRef?.current?.reset();

            if (reToken) {
              const resetPasswordPayload = {
                pessoaId: data.savedCliente.pessoa.id,
                email: objData.email,
                role: props.table,
                reToken,
              };

              try {
                const { data: initiateResetPasswordMessage } = await initiateResetPassword(
                  resetPasswordPayload,
                );
                toast.info(
                  <div dangerouslySetInnerHTML={{ __html: initiateResetPasswordMessage }} />,
                );
              } catch (error) {
                handleApiError(error);
              }
            }
          }

          return true;
        } else {
          setState({ ...state, openBackdrop: false });

          return false;
        }
      } else if (action === 'update') {
        const res = await update(props.table, selected[props.idColumn], cleanData);
        const data = res.data;
        setError(data.error ?? {});

        if (!data.error) {
          setState({
            ...state,
            openMsg: true,
            msg: 'Atualização realizada com sucesso!',
            alertSeverity: 'success',
          });
          setRefresh(!refresh);
          props.onClose();
          setOpen(false);
          props.setRefreshInput && props.setRefreshInput(true);

          return true;
        } else {
          setState({ ...state, openBackdrop: false });
          return false;
        }
      }
    } catch (err) {
      const error = err as AppError;
      setError(error.data.error);
      handleApiError(err);

      setState({ ...state, openBackdrop: false });
    }
  };

  const onChangeTextFieldValue = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: Fields,
  ) => {
    let newValue = e.target.value;

    if (field.shouldMask && masks[field.name]) {
      newValue = masks[field.name].apply(e.target.value);
    }

    setFieldValues({ ...fieldValues, [field.name]: newValue });

    if (field.customChangeText) {
      const customParams: any = {};

      const allParams: { [key: string]: any } = {
        newValue,
      };

      if (field.customChangeTextParamNames) {
        const customChangeTextParamNamesArr = Object.keys(field.customChangeTextParamNames);

        for (let i = 0; i < customChangeTextParamNamesArr.length; i++) {
          const paramName = field.customChangeTextParamNames[customChangeTextParamNamesArr[i]];

          customParams[customChangeTextParamNamesArr[i]] =
            functionMap[paramName] === undefined ? allParams[paramName] : functionMap[paramName];
        }
      }

      field.customChangeText(customParams);
    }
  };

  const handleTextInputStyle = (field: Fields): React.CSSProperties => {
    const isDisabledOnCreateOrUpdate =
      (field.disableOnCreate && action === 'create') ||
      (field.disableOnEdit && action === 'update');

    if (field.disabled || isDisabledOnCreateOrUpdate) {
      return {
        opacity: 0.5,
        cursor: 'not-allowed',
        pointerEvents: 'none',
      };
    }

    return {};
  };

  const resetFields = async () => {
    const fields = await getPropsFields(selected);
    const clearedValues: Record<string, string> = {};

    fields.forEach((field) => {
      if (field.name !== 'cpf' && field.name !== 'codFazenda') {
        clearedValues[field.name] = '';
      }
    });

    setFieldValues((prev: any) => ({ ...prev, ...clearedValues }));
  };

  const enableOrDisableFields = async (fields: string[], disable: boolean) => {
    const fieldsFromProps = await getPropsFields(selected);

    fields.forEach((field) => {
      const currentField = fieldsFromProps.find((f: Fields) => f.name === field);
      if (currentField) {
        currentField.disabled = disable;
        currentField.disableOnCreate = disable;
        currentField.disableOnEdit = disable;
      }
    });

    setNewFields(fieldsFromProps);
  };

  const functionMap: {
    [key: string]:
      | Dispatch<any>
      | (() => void)
      | Dispatch<SetStateAction<State>>
      | ((fields: string[], disabled: boolean) => void)
      | State;
  } = {
    setFieldValues,
    resetFields,
    setState,
    state,
    enableOrDisableFields,
  };

  useEffect(() => {
    if (open) handleOpenModal();
  }, [open]);

  useEffect(() => {
    if (props.open) setOpen(true);
  }, [props.open]);

  // useEffect(() => {
  //   const fetchAndUpdateFields = async () => {
  //     const updatedFields = await getFields();

  //     setNewFields(updatedFields);
  //   };

  //   fetchAndUpdateFields();
  // }, [props.getFields, selectedFazenda]);

  // useEffect(() => {
  //   console.log(fieldValues);
  // }, [fieldValues]);

  return (
    <ModalDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={action === 'create' ? 'Novo registro' : 'Editar registro'}
      dividers
      fullWidth
      maxWidth="lg"
    >
      <form ref={form}>
        <Grid container spacing={2}>
          {newFields.map(
            (field, index) =>
              ((props.action === 'create' && !field.hideOnCreate) ||
                (props.action === 'update' && !field.hideOnEdit)) && (
                <Grid
                  item
                  xs={field.xs ?? 12}
                  sm={field.sm}
                  md={field.md}
                  lg={field.lg}
                  xl={field.xl}
                  key={index}
                >
                  {field.type === 'text' || field.type === 'time' || field.type === 'hidden' ? (
                    // TODO: Resolver BUG no console
                    <TextField
                      required={field.isRequired}
                      hidden={field.type === 'hidden'}
                      type={field.type}
                      name={field.name}
                      id={field.name}
                      label={field.label}
                      value={fieldValues[field.name]}
                      onChange={(e) => onChangeTextFieldValue(e, field)}
                      defaultValue={fieldValues[field.name] ?? field?.defaultValue}
                      variant="outlined"
                      fullWidth
                      error={!!error[field.name]}
                      helperText={error[field.name] ?? ''}
                      style={handleTextInputStyle(field)}
                      InputLabelProps={{
                        shrink:
                          field.type === 'date' ||
                          field.type === 'time' ||
                          Boolean(fieldValues[field.name]) ||
                          Boolean(field.defaultValue),
                      }}
                    />
                  ) : field.type === 'date' ? (
                    <DatePicker
                      name={field.name}
                      label={field.label}
                      value={
                        fieldValues[field.name]
                          ? new Date(fieldValues[field.name])
                          : field.defaultValue
                      }
                      disablePast={props.action === 'create' && field.disablePastDates}
                      disableFuture={field.disableFutureDates}
                      defaultValue={field.defaultValue}
                      minDate={field.minDate}
                      maxDate={field.maxDate}
                      onChange={(date) => {
                        if (!date) return;
                        setFieldValues({ ...fieldValues, [field.name]: date });
                      }}
                      onError={(e) => {
                        if (!e) {
                          setDateErrors({ ...dateErrors, [field.name]: null });
                          return;
                        }
                        setDateErrors({ ...dateErrors, [field.name]: e });
                      }}
                      slotProps={{
                        textField: {
                          error: Boolean(!!error[field.name]) || !!dateErrors[field.name],
                          helperText: error[field.name] || getDateErrorMessage(field.name),
                        },
                      }}
                      sx={{ ...handleTextInputStyle(field), width: '100%' }}
                    />
                  ) : field.type === 'observacao' ? (
                    observacao.map((obs, i) => (
                      <Grid key={i} container mt={i > 0 ? 2 : 0}>
                        {/* Campos */}
                        <Grid item xs={11}>
                          <Observacao
                            key={i}
                            observacao={{
                              ...obs,
                              dataObservacao: obs.dataObservacao
                                ? new Date(obs.dataObservacao)
                                : new Date(),
                            }}
                            textField={{
                              value: fieldValues[field.name],
                              defaultValue: fieldValues[field.name]
                                ? undefined
                                : field?.defaultValue,
                              onChange: (e) => {
                                const novasObservacoes = observacao.slice();
                                novasObservacoes[i].descricao = e.target.value;
                                setObservacao(novasObservacoes);
                              },
                              error: !!error[field.name],
                              helperText: error[field.name] ?? '',
                              style: handleTextInputStyle(field),
                              InputLabelProps: {
                                shrink: Boolean(obs.descricao) || Boolean(field.defaultValue),
                              },
                            }}
                            datePicker={{
                              onChange: (novaData) => {
                                const novasObservacoes = observacao.slice();
                                novasObservacoes[i].dataObservacao = novaData
                                  ? novaData.toISOString()
                                  : null;
                                setObservacao(novasObservacoes);
                              },
                              onError: (e) => {
                                if (!e) {
                                  setDateErrors({ ...dateErrors, [field.name]: null });
                                  return;
                                }
                                setDateErrors({ ...dateErrors, [field.name]: e });
                              },
                              slotProps: {
                                textField: {
                                  error: Boolean(!!error[field.name]) || !!dateErrors[field.name],
                                  helperText: error[field.name] || getDateErrorMessage(field.name),
                                },
                              },
                              sx: { ...handleTextInputStyle(field), width: '100%' },
                            }}
                          />
                        </Grid>
                        {/* Botões */}
                        <Grid
                          item
                          xs={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {i > 0 ? (
                            <IconButton
                              color="error"
                              onClick={() => removeObservacao(i)}
                              aria-label="remove observacao"
                              size="large"
                              sx={{ borderRadius: '5px' }}
                            >
                              <RxMinus />
                            </IconButton>
                          ) : (
                            <IconButton
                              color="success"
                              onClick={addObservacao}
                              aria-label="add observacao"
                              size="large"
                              sx={{ borderRadius: '5px' }}
                            >
                              <RxPlus />
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    ))
                  ) : field.type === 'image' ? (
                    <InputImage
                      name={field.name}
                      id={field.name}
                      label={field.label}
                      value={fieldValues[field.name]}
                      defaultValue={field.defaultValue}
                      error={!!error[field.name]}
                      helperText={error[field.name] ?? ''}
                      onImageChange={(file) => {
                        setFieldValues({ ...fieldValues, [field.name]: file });
                      }}
                      InputLabelProps={{
                        shrink:
                          field.type === 'date' ||
                          field.type === 'time' ||
                          Boolean(fieldValues[field.name]) ||
                          Boolean(field.defaultValue),
                      }}
                    />
                  ) : field.type === 'inputSelect' ? (
                    <AddInputButtonContainer>
                      <InputSelect
                        name={field.name}
                        id={field.name}
                        options={typeof field.options !== 'string' ? field.options ?? [] : []}
                        remotedata={typeof field.options === 'string' ? field.options : undefined}
                        getOptionLabel={field.optionLabel}
                        renderOption={field.renderOption}
                        selectedvalue={
                          fieldValues[field.fieldid ?? field.name] ?? field.defaultValue
                        }
                        fieldid={field.fieldid}
                        style={handleTextInputStyle(field)}
                        refreshinput={refreshInput}
                        setrefreshinput={setRefreshInput}
                        onSelectedValue={async () => {
                          const cleanData = await getCleanData();
                          const fields = await getPropsFields(cleanData);
                          setNewFields(fields);
                        }}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            label={field.label}
                            variant="outlined"
                            error={!!error[field.name]}
                            helperText={error[field.name] ?? ''}
                          />
                        )}
                      />

                      {field.creationModal && (
                        <ButtonCreateModalDialog
                          table={field.table ?? ''}
                          idColumn={field.idColumn ?? ''}
                          getFields={field.getFields}
                          setRefreshInput={setRefreshInput}
                          state={state}
                          setState={setState}
                        />
                      )}
                    </AddInputButtonContainer>
                  ) : null}
                </Grid>
              ),
          )}
        </Grid>
      </form>

      <ReCAPTCHA
        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
        size="invisible"
        ref={reRef as React.RefObject<ReCAPTCHA>}
      />
    </ModalDialog>
  );
}
